'use no memo';
// AIDEV-NOTE: 'use no memo' - This hook uses intentional patterns (refs accessed in useMemo
// with version counter for forced recalculation) that conflict with React Compiler assumptions.
// The pendingVersion pattern is a deliberate optimization for high-frequency optimistic updates.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InteractionTarget, InteractionType } from '@models/api/interaction';
import { QuestionVisibility } from '@models/api/question';
import type { InteractionCreate, InteractionToggleResponse } from '@models/api';
import { createInteractionApiV1InteractionsPost } from '@api-client/api-client';
import type { AxiosResponse } from 'axios';
import { useToast } from './use-toast';
import {
  questionDetailKey,
  questionInteractionsKey,
  interactionsKey,
  updateQuestionEntity,
  getQuestionEntity,
  captureSnapshot,
  restoreSnapshot,
  addUserToInteractionList,
  removeUserFromInteractionList,
  updateInteractionsQuery,
  adjustQuestionCounters,
  setQuestionFlag,
  invalidateQuestionCaches,
} from '@lib/cache/questions';
import type { InteractionFlagState, InteractionSnapshot } from '@lib/cache/questions';
import { cancelEntityProducers } from '@lib/cache/entityProducers';
import { useAuth } from '@providers';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import type { QuestionCacheItem } from '@lib/cache/questions';

const MAX_UPLIFT_CLICKS = 10;
const PENDING_TIMEOUT_MS = 5000;
const UPLIFT_INVALIDATE_DEBOUNCE_MS = 600;

const upliftClickMemory = new Map<string, number>();

// AIDEV-NOTE: Single-source optimistic interactions with sequence tracking per interaction-race-condition-fix plan

interface QuestionInteractionInput {
  id: string;
  question?: {
    event_ids?: string[];
    is_my_question?: boolean;
    user_has_me_too?: boolean;
    user_has_i_can_help?: boolean;
    user_has_bookmarked?: boolean;
    visibility?: QuestionVisibility;
    slug?: string | null;
  };
  interactions?: {
    me_too?: import('@models/api').UserSummary[];
    i_can_help?: import('@models/api').UserSummary[];
  } | null;
}

interface UseQuestionInteractionsOptimisticResult {
  userHasUpvoted: boolean;
  userHasMeToo: boolean;
  userHasBookmarked: boolean;
  userHasICanHelp: boolean;
  upliftClickCount: number;
  upliftLimitReached: boolean;
  interactionsLoading: boolean;
  isOwnQuestion: boolean;
  createPending: boolean;
  deletePending: boolean;
  handleUpvote: () => void;
  handleMeToo: () => void;
  handleBookmark: () => void;
  handleICanHelp: (content?: string) => void;
}

interface PendingToggleEntry {
  sequence: number;
  value: boolean;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface MutationContext {
  snapshot: InteractionSnapshot;
  interactionType: InteractionType;
  sequence: number;
}

const TOGGLEABLE_TYPES = new Set<InteractionType>([
  InteractionType.me_too,
  InteractionType.i_can_help,
  InteractionType.bookmark,
]);

const FLAG_CONFIG: Record<
  InteractionType,
  { key?: keyof InteractionFlagState; affectsCount: boolean }
> = {
  [InteractionType.uplift]: { affectsCount: true },
  [InteractionType.me_too]: { key: 'meToo', affectsCount: true },
  [InteractionType.i_can_help]: { key: 'iCanHelp', affectsCount: true },
  [InteractionType.bookmark]: { key: 'bookmark', affectsCount: false },
  [InteractionType.view]: { affectsCount: false },
  [InteractionType.we_met]: { affectsCount: false },
  [InteractionType.remember]: { affectsCount: false },
  [InteractionType.added_note]: { affectsCount: false },
};

// Entity-store field carrying each toggleable flag (used by the onSuccess
// reconcile — FLAG_CONFIG.key names the *local* InteractionFlagState field,
// which is a different naming scheme).
const ENTITY_FLAG_FIELDS = {
  [InteractionType.me_too]: 'user_has_me_too',
  [InteractionType.i_can_help]: 'user_has_i_can_help',
  [InteractionType.bookmark]: 'user_has_bookmarked',
} as const;

export const normalizeEventIds = (eventIds?: string[]) => {
  if (!eventIds) return [] as string[];
  return eventIds.filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
};

export const useQuestionInteractionsOptimistic = (
  question: QuestionInteractionInput,
): UseQuestionInteractionsOptimisticResult => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const normalizedEventIds = useMemo(
    () => normalizeEventIds(question.question?.event_ids),
    [question.question?.event_ids],
  );
  const questionVisibility = question.question?.visibility;
  const shouldInvalidateMeetverse = questionVisibility === QuestionVisibility.anyone;
  const questionSlug = question.question?.slug ?? null;

  const handleInvalidateQuestionCaches = useCallback(() => {
    invalidateQuestionCaches({
      queryClient,
      eventIds: normalizedEventIds,
      slug: questionSlug,
      shouldInvalidateMeetverse,
    });
  }, [normalizedEventIds, queryClient, questionSlug, shouldInvalidateMeetverse]);

  const interactionsLists = question.interactions;

  // AIDEV-NOTE: Refs must be declared BEFORE any useMemo/useCallback that references them
  const pendingTogglesRef = useRef<Map<InteractionType, PendingToggleEntry>>(new Map());
  const sequenceRef = useRef(0);
  const upliftInvalidateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpliftCountRef = useRef(0);

  // AIDEV-NOTE: Force update counter - included in useMemo deps to trigger recalculation when pendingToggles change
  const [pendingVersion, setPendingVersion] = useState(0);
  const triggerUpdate = useCallback(() => {
    setPendingVersion(n => n + 1);
  }, []);

  const baseFlags = useMemo<InteractionFlagState>(() => {
    const userId = user?.id;
    const meTooFromInteractions = userId
      ? (interactionsLists?.me_too?.some(entry => entry.id === userId) ?? false)
      : false;
    const iCanHelpFromInteractions = userId
      ? (interactionsLists?.i_can_help?.some(entry => entry.id === userId) ?? false)
      : false;

    return {
      meToo: (question.question?.user_has_me_too ?? false) || meTooFromInteractions,
      iCanHelp: (question.question?.user_has_i_can_help ?? false) || iCanHelpFromInteractions,
      bookmark: question.question?.user_has_bookmarked ?? false,
    };
  }, [
    interactionsLists?.i_can_help,
    interactionsLists?.me_too,
    question.question?.user_has_bookmarked,
    question.question?.user_has_i_can_help,
    question.question?.user_has_me_too,
    user?.id,
  ]);

  // AIDEV-NOTE: Derive optimistic flags directly - pendingVersion dependency ensures recalculation on ref changes
  // Pending toggles take precedence over base flags from server
  /* eslint-disable react-hooks/refs -- Intentional pattern: refs accessed in useMemo with version counter for forced recalculation */
  const optimisticFlags = useMemo<InteractionFlagState>(() => {
    // AIDEV-NOTE: pendingVersion forces recalculation when pendingTogglesRef mutates.
    void pendingVersion;
    const hasPendingMeToo = pendingTogglesRef.current.has(InteractionType.me_too);
    const hasPendingICanHelp = pendingTogglesRef.current.has(InteractionType.i_can_help);
    const hasPendingBookmark = pendingTogglesRef.current.has(InteractionType.bookmark);

    return {
      meToo: hasPendingMeToo
        ? (pendingTogglesRef.current.get(InteractionType.me_too)?.value ?? baseFlags.meToo)
        : baseFlags.meToo,
      iCanHelp: hasPendingICanHelp
        ? (pendingTogglesRef.current.get(InteractionType.i_can_help)?.value ?? baseFlags.iCanHelp)
        : baseFlags.iCanHelp,
      bookmark: hasPendingBookmark
        ? (pendingTogglesRef.current.get(InteractionType.bookmark)?.value ?? baseFlags.bookmark)
        : baseFlags.bookmark,
    };
  }, [baseFlags, pendingVersion]);
  /* eslint-enable react-hooks/refs */

  // Keep ref in sync for snapshot/restore operations
  /* eslint-disable react-hooks/refs -- Intentional ref sync for optimistic update snapshots */
  const optimisticFlagsRef = useRef<InteractionFlagState>(optimisticFlags);
  optimisticFlagsRef.current = optimisticFlags;
  /* eslint-enable react-hooks/refs */

  // AIDEV-NOTE: Cleanup effect is legitimate - clearing timeouts is an external system interaction
  // Capture refs at effect setup time per ESLint react-hooks/exhaustive-deps rule
  useEffect(() => {
    const pendingToggles = pendingTogglesRef.current;
    const upliftTimeout = upliftInvalidateTimeoutRef;

    return () => {
      pendingToggles.forEach(entry => {
        clearTimeout(entry.timeoutId);
      });
      pendingToggles.clear();

      if (upliftTimeout.current) {
        clearTimeout(upliftTimeout.current);
        upliftTimeout.current = null;
      }

      pendingUpliftCountRef.current = 0;
    };
  }, []);

  const recordPendingToggle = useCallback(
    (interactionType: InteractionType, value: boolean, sequence: number) => {
      if (!TOGGLEABLE_TYPES.has(interactionType)) return;

      const existing = pendingTogglesRef.current.get(interactionType);
      if (existing) {
        clearTimeout(existing.timeoutId);
      }

      const timeoutId = setTimeout(() => {
        const entry = pendingTogglesRef.current.get(interactionType);
        if (!entry || entry.sequence !== sequence) return;
        pendingTogglesRef.current.delete(interactionType);
        triggerUpdate(); // Re-render to reflect cleared pending state
      }, PENDING_TIMEOUT_MS);

      pendingTogglesRef.current.set(interactionType, { sequence, value, timeoutId });
      triggerUpdate(); // Re-render to reflect new pending state
    },
    [triggerUpdate],
  );

  // AIDEV-NOTE: clearPendingToggle can optionally skip the re-render trigger for batching
  const clearPendingToggle = useCallback(
    (interactionType: InteractionType, sequence?: number, shouldTrigger = true) => {
      const entry = pendingTogglesRef.current.get(interactionType);
      if (!entry) return;
      if (typeof sequence === 'number' && entry.sequence !== sequence) return;
      clearTimeout(entry.timeoutId);
      pendingTogglesRef.current.delete(interactionType);
      if (shouldTrigger) {
        triggerUpdate(); // Re-render to reflect cleared pending state
      }
    },
    [triggerUpdate],
  );

  const applyQuestionDelta = useCallback(
    (interactionType: InteractionType, options: { delta?: number; toggledOn?: boolean }) => {
      const { delta = 0, toggledOn } = options;

      updateQuestionEntity(queryClient, question.id, (existing: QuestionCacheItem) => {
        const next = { ...existing };

        if (typeof toggledOn === 'boolean') {
          setQuestionFlag(next, interactionType, toggledOn);
        }

        if (delta !== 0) {
          adjustQuestionCounters(next, interactionType, delta);
        }

        return next;
      });
    },
    [queryClient, question.id],
  );

  const notifyInteractionError = useCallback(
    (interactionType: InteractionType | undefined, phase: 'create' | 'delete') => {
      if (!interactionType) return;
      const action = phase === 'create' ? 'add' : 'remove';
      const messages: Partial<Record<InteractionType, string>> = {
        [InteractionType.uplift]: `Failed to ${action} upvote`,
        [InteractionType.me_too]: `Failed to ${action} "me too"`,
        [InteractionType.bookmark]: `Failed to ${action} bookmark`,
        [InteractionType.i_can_help]: `Failed to ${action} "I can help"`,
      };
      const title = messages[interactionType] ?? 'Interaction failed';
      toast({ title, description: 'Please try again.', variant: 'error' });
    },
    [toast],
  );

  // AIDEV-NOTE: Track question.id changes to reset uplift count from memory - no useEffect needed
  /* eslint-disable react-hooks/refs -- Intentional pattern: refs used to track previous values during render */
  const prevQuestionIdRef = useRef(question.id);
  const upliftClickCountRef = useRef(upliftClickMemory.get(question.id) ?? 0);

  // Reset on question.id change (during render, not in effect)
  if (prevQuestionIdRef.current !== question.id) {
    prevQuestionIdRef.current = question.id;
    upliftClickCountRef.current = upliftClickMemory.get(question.id) ?? 0;
  }

  // Use forceUpdate to trigger re-render when count changes
  const upliftClickCount = upliftClickCountRef.current;
  /* eslint-enable react-hooks/refs */
  const upliftLimitReached = upliftClickCount >= MAX_UPLIFT_CLICKS;

  const updateUpliftCount = useCallback(
    (updater: (prev: number) => number) => {
      const next = Math.max(0, Math.min(MAX_UPLIFT_CLICKS, updater(upliftClickCountRef.current)));
      upliftClickCountRef.current = next;
      upliftClickMemory.set(question.id, next);
      triggerUpdate();
    },
    [question.id, triggerUpdate],
  );

  const scheduleQuestionRefresh = useCallback(() => {
    if (upliftInvalidateTimeoutRef.current) {
      clearTimeout(upliftInvalidateTimeoutRef.current);
    }

    upliftInvalidateTimeoutRef.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: questionDetailKey(question.id) });
      void queryClient.invalidateQueries({ queryKey: interactionsKey() });
      handleInvalidateQuestionCaches();
      upliftInvalidateTimeoutRef.current = null;
    }, UPLIFT_INVALIDATE_DEBOUNCE_MS);
  }, [handleInvalidateQuestionCaches, queryClient, question.id]);

  // AIDEV-NOTE: Assign refs directly during render - no useEffect needed (refs don't trigger re-renders)
  /* eslint-disable react-hooks/refs -- Intentional pattern: stable refs for callback closures */
  const questionIdRef = useRef(question.id);
  const questionSlugRef = useRef(questionSlug);
  const userRef = useRef(user);
  questionIdRef.current = question.id;
  questionSlugRef.current = questionSlug;
  userRef.current = user;
  /* eslint-enable react-hooks/refs */

  // AIDEV-NOTE: Using useMutation directly with stable callback refs per TanStack Query v5 best practices
  const createMutation = useMutation<
    AxiosResponse<InteractionToggleResponse>,
    Error,
    { data: InteractionCreate },
    MutationContext
  >({
    mutationFn: ({ data }) => createInteractionApiV1InteractionsPost(data),

    // onMutate: Called immediately when mutate() is called - apply optimistic update
    onMutate: async variables => {
      const data = variables.data;

      const currentQuestionId = questionIdRef.current;
      const currentUser = userRef.current
        ? {
            id: userRef.current.id,
            full_name: userRef.current.full_name,
            profile_picture: userRef.current.profile_picture ?? null,
            slug: userRef.current.slug,
          }
        : null;
      const interactionType = data.interaction_type;
      const sequence = ++sequenceRef.current;

      // Cancel every in-flight query that writes to the entity store for this
      // question, so a producer's refetch can't land after this optimistic
      // write and clobber it (upsertQuestionEntities overwrites unconditionally).
      await cancelEntityProducers(queryClient, currentQuestionId);

      // Snapshot the previous value for rollback
      const snapshot = captureSnapshot(queryClient, currentQuestionId, optimisticFlagsRef.current);

      const config = FLAG_CONFIG[interactionType];
      const previousState = config.key ? optimisticFlagsRef.current[config.key] : undefined;
      let predictedState: boolean | undefined;
      let delta = 0;

      // Optimistically update for toggleable interactions
      if (TOGGLEABLE_TYPES.has(interactionType) && typeof previousState === 'boolean') {
        predictedState = !previousState;

        // Record pending toggle with the predicted value (creates entry and triggers single re-render)
        recordPendingToggle(interactionType, predictedState, sequence);

        // Calculate delta for count updates
        if (config.affectsCount && previousState !== predictedState) {
          delta = predictedState ? 1 : -1;
        }

        // Apply optimistic update to query cache
        applyQuestionDelta(interactionType, {
          delta,
          toggledOn: predictedState,
        });

        // Update user lists in cache
        if (predictedState) {
          addUserToInteractionList(
            queryClient,
            currentQuestionId,
            interactionType,
            currentUser,
            data.content ?? undefined,
          );
        } else {
          removeUserFromInteractionList(
            queryClient,
            currentQuestionId,
            interactionType,
            currentUser?.id,
          );
        }

        updateInteractionsQuery(
          queryClient,
          currentQuestionId,
          interactionType,
          currentUser?.id,
          predictedState,
          undefined,
          data.content ?? null,
        );
      } else if (interactionType === InteractionType.uplift) {
        delta = 1;
        applyQuestionDelta(interactionType, { delta });
        pendingUpliftCountRef.current += 1;
      }

      // Return context for use in onError/onSuccess/onSettled
      return {
        snapshot,
        interactionType,
        sequence,
      };
    },

    // onSuccess: Server confirmed the mutation - reconcile if needed
    onSuccess: (response, variables, context) => {
      const currentQuestionId = questionIdRef.current;
      const currentUser = userRef.current
        ? {
            id: userRef.current.id,
            full_name: userRef.current.full_name,
            profile_picture: userRef.current.profile_picture ?? null,
            slug: userRef.current.slug,
          }
        : null;
      const data = response.data;
      const interactionType = variables.data.interaction_type;

      // Decrement first — every completed uplift mutation must decrement the pending
      // counter, even stale ones, to prevent the ref from leaking above zero.
      // If the decrement were placed after the stale-sequence guard, a stale mutation
      // would skip the decrement and leave pendingUpliftCountRef.current ≥ 1 forever,
      // causing scheduleQuestionRefresh (gated on === 0) to never fire for that card.
      if (interactionType === InteractionType.uplift) {
        pendingUpliftCountRef.current = Math.max(0, pendingUpliftCountRef.current - 1);
      }

      // Check for stale mutation (another mutation happened after this one started)
      if (context.sequence < sequenceRef.current) {
        // Don't process stale mutations - let the newer mutation handle state
        return;
      }

      if (TOGGLEABLE_TYPES.has(interactionType)) {
        // Use server response as source of truth, fallback to prediction
        const toggledOn = data.toggled_on;

        // Clear pending without triggering re-render (query cache update will reflect in baseFlags)
        clearPendingToggle(interactionType, context.sequence, false);

        // Reconcile against the entity's CURRENT state, not the onMutate-time
        // prediction: a producer refetch that started after cancelEntityProducers
        // ran can land mid-flight and overwrite the optimistic write with
        // pre-commit server data. When no clobber happened this is equivalent
        // to the old prediction-delta check (flag and count always move
        // together through both write paths).
        const entity = getQuestionEntity(queryClient, currentQuestionId);
        const flagField =
          interactionType in ENTITY_FLAG_FIELDS
            ? ENTITY_FLAG_FIELDS[interactionType as keyof typeof ENTITY_FLAG_FIELDS]
            : undefined;
        if (entity && flagField && entity[flagField] !== toggledOn) {
          applyQuestionDelta(interactionType, {
            delta: FLAG_CONFIG[interactionType].affectsCount ? (toggledOn ? 1 : -1) : 0,
            toggledOn,
          });
        }

        // Update interaction lists with server data
        if (toggledOn) {
          addUserToInteractionList(
            queryClient,
            currentQuestionId,
            interactionType,
            currentUser,
            data.interaction?.content ?? variables.data.content ?? undefined,
          );
        } else {
          removeUserFromInteractionList(
            queryClient,
            currentQuestionId,
            interactionType,
            currentUser?.id,
          );
        }

        updateInteractionsQuery(
          queryClient,
          currentQuestionId,
          interactionType,
          currentUser?.id,
          toggledOn,
          data.interaction,
          data.interaction?.content ?? variables.data.content ?? null,
        );
        // AIDEV-NOTE: No explicit triggerUpdate() needed here - setQueryData calls above
        // trigger React Query subscribers to re-render, which recalculates optimisticFlags
      }

      // scheduleQuestionRefresh is called only by the winning (non-stale) mutation.
      // The ref was already decremented above, before the stale-sequence guard.
      if (interactionType === InteractionType.uplift) {
        if (pendingUpliftCountRef.current === 0) {
          scheduleQuestionRefresh();
        }
      }
      // AIDEV-NOTE: Do NOT invalidate queries here for toggleable types - optimistic update is already applied
      // Invalidation would cause a refetch that may race with the optimistic state
    },

    // onError: Mutation failed - rollback to snapshot
    onError: (error, variables, context) => {
      const currentQuestionId = questionIdRef.current;

      // sanitizeError strips raw Axios `config`/`request` (which can carry
      // Authorization headers + request body) before serializing into the
      // browser console / Sentry breadcrumb stream.
      logger.error('useQuestionInteractionsOptimistic: mutation failed', {
        error: sanitizeError(error),
        interactionType: variables.data.interaction_type,
        targetType: variables.data.target_type,
        targetId: variables.data.target_id,
      });

      if (!context) {
        notifyInteractionError(variables.data.interaction_type, 'create');
        return;
      }

      // Decrement first — mirrors the onSuccess fix: every completed (or failed)
      // uplift mutation must decrement the pending counter before the stale-sequence
      // guard can return early, so the ref never leaks above zero.
      if (context.interactionType === InteractionType.uplift) {
        pendingUpliftCountRef.current = Math.max(0, pendingUpliftCountRef.current - 1);
        // Same rule for the click-limit memory: handleUpvote counted this tap
        // when it dispatched, so a failed tap must be uncounted even when a
        // newer in-flight mutation owns the visible state (guard below).
        updateUpliftCount(prev => Math.max(0, prev - 1));
      }

      // Mirror the stale-mutation guard from onSuccess: a slow failure for an
      // older sequence must not clobber state owned by a newer in-flight one.
      if (context.sequence < sequenceRef.current) {
        return;
      }

      clearPendingToggle(context.interactionType, context.sequence);

      // Rollback to the snapshot taken in onMutate. restoreSnapshot does not
      // touch pendingTogglesRef — we only cleared the failed type above; other
      // in-flight toggles (e.g. a parallel i_can_help mutation) stay pending.
      restoreSnapshot(queryClient, currentQuestionId, context.snapshot, triggerUpdate);

      notifyInteractionError(context.interactionType, 'create');
    },

    // onSettled: Called after either success or error - good place for eventual consistency invalidation
    onSettled: (_data, error, variables) => {
      const interactionType = variables.data.interaction_type;

      // For toggleable types, schedule a delayed invalidation to ensure eventual consistency
      // This won't affect the immediate optimistic UI but ensures data is fresh
      if (TOGGLEABLE_TYPES.has(interactionType) && !error) {
        // AIDEV-NOTE: Capture current questionId to avoid stale closure in setTimeout callback
        const currentQuestionId = questionIdRef.current;
        // Use a longer delay for toggleable types to let the optimistic update be visible
        setTimeout(() => {
          void queryClient.invalidateQueries({
            queryKey: questionInteractionsKey(currentQuestionId),
            refetchType: 'none', // Don't refetch immediately, just mark as stale
          });
        }, 1000);
      }
    },
  });

  const isOwnQuestion = question.question?.is_my_question ?? false;

  const lastInteractionTime = useRef<Record<string, number>>({});
  const DEBOUNCE_MS = 300;

  const toggleInteraction = useCallback(
    (interactionType: InteractionType, content?: string): boolean => {
      if (!user || isOwnQuestion) return false;

      if (interactionType === InteractionType.uplift && upliftLimitReached) {
        return false;
      }

      const key = `${question.id}-${interactionType}`;
      const now = Date.now();
      const previous = lastInteractionTime.current[key] ?? 0;
      if (now - previous < DEBOUNCE_MS) {
        return false;
      }
      lastInteractionTime.current[key] = now;

      createMutation.mutate({
        data: {
          user_id: user.id,
          target_type: InteractionTarget.question,
          target_id: question.id,
          interaction_type: interactionType,
          content,
        },
      });

      return true;
    },
    [createMutation, isOwnQuestion, question.id, upliftLimitReached, user],
  );

  const handleUpvote = useCallback(() => {
    if (upliftLimitReached) return;
    // Only count a tap that actually fires a mutation — toggleInteraction's
    // debounce guard silently drops rapid double-taps, and the click counter
    // must not drift ahead of the real uplift count it gates MAX_UPLIFT_CLICKS
    // against.
    if (toggleInteraction(InteractionType.uplift)) {
      updateUpliftCount(prev => prev + 1);
    }
  }, [toggleInteraction, upliftLimitReached, updateUpliftCount]);

  const handleMeToo = useCallback(() => {
    toggleInteraction(InteractionType.me_too);
  }, [toggleInteraction]);

  const handleBookmark = useCallback(() => {
    toggleInteraction(InteractionType.bookmark);
  }, [toggleInteraction]);

  const handleICanHelp = useCallback(
    (content?: string) => {
      toggleInteraction(InteractionType.i_can_help, content);
    },
    [toggleInteraction],
  );

  /* eslint-disable react-hooks/refs -- Intentional: optimisticFlags derives from refs with version counter */
  return {
    userHasUpvoted: false,
    userHasMeToo: optimisticFlags.meToo,
    userHasBookmarked: optimisticFlags.bookmark,
    userHasICanHelp: optimisticFlags.iCanHelp,
    upliftClickCount,
    upliftLimitReached,
    interactionsLoading: false,
    isOwnQuestion,
    createPending: createMutation.isPending,
    deletePending: false,
    handleUpvote,
    handleMeToo,
    handleBookmark,
    handleICanHelp,
  };
  /* eslint-enable react-hooks/refs */
};
